<template>
    <div>
        <v-expansion-panel class="journey-details" expand v-model="expanded">
            <v-expansion-panel-content>
                <template v-slot:header>
                    <h2>Journey Details</h2>
                </template>

                <div class="journey-details__item">
                    <div class="journey-details__icon">
                        <icon icon="pin-grey"></icon>
                    </div>
                    <div class="journey-details__name">Start location</div>
                    <div class="journey-details__value">{{ startPoint.address.label || 'Unknown' }}</div>
                </div>

                <div class="journey-details__item">
                    <div class="journey-details__icon">
                        <icon icon="calendar-small"></icon>
                    </div>
                    <div class="journey-details__name">Start time</div>
                    <div class="journey-details__value">{{ formatDate(startPoint.utc, 'DD/MM/YYYY HH:mm:ss') }}</div>
                </div>

                <div class="journey-details__item">
                    <div class="journey-details__icon">
                        <icon icon="pin-grey"></icon>
                    </div>
                    <div class="journey-details__name">End location</div>
                    <div class="journey-details__value">{{ endPoint.address.label || 'Unknown' }}</div>
                </div>

                <div class="journey-details__item">
                    <div class="journey-details__icon">
                        <icon icon="calendar-small"></icon>
                    </div>
                    <div class="journey-details__name">End time</div>
                    <div class="journey-details__value">{{ formatDate(endPoint.utc, 'DD/MM/YYYY HH:mm:ss') }}</div>
                </div>

                <div class="journey-details__item">
                    <div class="journey-details__icon">
                        <icon icon="compass-solid"></icon>
                    </div>
                    <div class="journey-details__name">Distance</div>
                    <div class="journey-details__value">{{ resolveDistance() }}</div>
                </div>

                <div class="journey-details__item">
                    <div class="journey-details__icon">
                        <icon icon="clock-solid"></icon>
                    </div>
                    <div class="journey-details__name">Duration</div>
                    <div class="journey-details__value">{{ duration }}</div>
                </div>
                <div class="journey-details__item" v-if="isDebug">
                    <div class="journey-details__icon">
                        <icon icon="clock-solid"></icon>
                    </div>
                    <div class="journey-details__name">Id</div>
                    <div class="journey-details__value">{{ journeyId }}</div>
                </div>
                <div class="journey-details__item" v-if="isDebug">
                    <div class="journey-details__icon">
                        <icon icon="clock-solid"></icon>
                    </div>
                    <div class="journey-details__name">Vehicle id</div>
                    <div class="journey-details__value">{{ vehicleId || 'N/A' }}</div>
                </div>
                <div class="journey-details__item" v-if="isDebug && vehicleId">
                    <div class="journey-details__icon">
                        <icon icon="clock-solid"></icon>
                    </div>
                    <div class="journey-details__name">Link</div>
                    <div class="journey-details__value">
                        <router-link :to="link">Go to</router-link>
                    </div>
                </div>
                <div class="journey-details__map-settings">
                    <v-switch
                        v-model="showIdlePoints"
                        label="Show Idle Points"
                        hide-details
                    ></v-switch>
                </div>
                <div class="journey-details__map-settings">
                    <v-switch
                        v-model="showRawData"
                        label="Show Raw Data"
                        :disabled="!hasRawData"
                        hide-details
                    ></v-switch>
                </div>
                <div class="journey-details__map-settings" v-if="hasSpeedingData">
                    <v-switch
                        v-model="showSpeedingData"
                        label="Show Speeding Data"
                        hide-details
                    ></v-switch>
                </div>

            </v-expansion-panel-content>
        </v-expansion-panel>
    </div>
</template>

<script>

import Icon from '@/components/atoms/Icon'
import moment from 'moment'
import JourneyPoint from '@/models/JourneyPoint'
import { speedOrDistanceUnitManager } from '@/helpers/Utils'
import { mapGetters } from 'vuex'

export default {
    name: 'JourneyDetails',
    components: {
        Icon
    },
    props: {
        startPoint: {
            type: JourneyPoint,
            required: false
        },
        endPoint: {
            type: JourneyPoint,
            required: true
        },
        distance: {
            type: String,
            required: true
        },
        duration: {
            type: String,
            required: true
        },
        journeyId: {
            type: String,
            required: true
        },
        vehicleId: {
            type: [String, null],
            required: false
        },
        hardwareId: {
            type: String,
            required: true
        },
        deviceDetails: {
            type: Object,
            required: false
        },
        hasRawData: {
            type: Boolean,
            required: true,
        },
        hasSpeedingData: {
            type: Boolean,
            required: true,
        },
        currentLengthMeters: {
            type: Number,
            default: false,
            required: false,
        },
        isCurrentJourney: {
            type: Boolean,
            required: true,
        }
    },
    data () {
        return {
            expanded: [true],
            isLiveStreaming: false,
            isLivePreviewModal: false,
            livePreviewStatus: null,
            livePreviewButtonTest: 'Live view',
            activeDevice: null,
            availableChannelsFlag: 0
        }
    },
    computed: {
        ...mapGetters({

        }),
        showIdlePoints: {
            get () {
                return this.$store.state.trackingModule.showIdlePoints
            },
            set (value) {
                this.$store.commit('trackingModule/setShowIdlePoints', value)
            }
        },
        showRawData: {
            get () {
                return this.$store.state.trackingModule.showRawData
            },
            set (value) {
                this.$store.commit('trackingModule/setShowRawData', value)
            }
        },
        showSpeedingData: {
            get () {
                return this.$store.state.trackingModule.showSpeedingData
            },
            set (value) {
                this.$store.commit('trackingModule/setShowSpeedingData', value)
            }
        },
        isDebug () {
            return process.env.VUE_APP_DEBUG
        },
        link () {
            let date = moment(this.startPoint.utc).format('YYYY-MM-DD')
            return `dashboard/${this.vehicleId}/${this.journeyId}/${date}`
        },
    },
    methods: {
        formatDate (date, format) {
            return moment(date).format(format)
        },
        onLiveStreaming (status) {
            this.isLiveStreaming = status
        },
        resolveDistance() {
            return this.isCurrentJourney ? speedOrDistanceUnitManager(this.currentLengthMeters / 1000, 'distance') : this.distance;
        },
        calendarProcessed () {
            this.remotePlaybackPreviewStatus = null
            this.remotePlaybackPreviewButtonText = 'Remote playback'

            if (null !== this.remotePlaybackTimeout) {
                clearTimeout(this.remotePlaybackTimeout)
            }
        }
    }
}
</script>

<style scoped lang="scss">
.journey-details {
    display: flex;
    flex-direction: column;

    &__item {
        display: flex;
        font-size: 12px;
        margin: 2px 0;
    }

    &__icon {
        width: 20px;
        margin-right: 5px;
        color: $inactive;
        display: flex;
        flex-grow: 0;
        flex-shrink: 0;
    }

    &__name {
        width: 75px;
        display: flex;
        flex-grow: 0;
        flex-shrink: 0;
        border-right: 1px solid $inactive;
    }

    &__value {
        padding-left: 4px;
    }
}
</style>
